import React, { useEffect, useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import { Sparkles, RefreshCcw, Activity } from "lucide-react";
import client, { fmtPct } from "../lib/api";

const cellColor = (v) => {
  if (v === null || v === undefined) return { bg: "rgba(255,255,255,0.04)", fg: "#525252" };
  // -1..1 → red-neutral-green diverging
  const t = (v + 1) / 2; // 0..1
  // We blend between rose-500, neutral-700, emerald-500
  const lerp = (a, b, t) => Math.round(a + (b - a) * t);
  let r, g, b;
  if (t < 0.5) {
    const k = t * 2; // 0..1 within bearish half
    r = lerp(239, 64, k);
    g = lerp(68, 64, k);
    b = lerp(68, 64, k);
  } else {
    const k = (t - 0.5) * 2;
    r = lerp(64, 16, k);
    g = lerp(64, 185, k);
    b = lerp(64, 129, k);
  }
  const alpha = 0.18 + Math.min(0.55, Math.abs(v) * 0.7);
  return {
    bg: `rgba(${r},${g},${b},${alpha})`,
    fg: Math.abs(v) > 0.45 ? "#ffffff" : "#e5e5e5",
  };
};

const RANGES = [
  { d: 30, label: "1M" },
  { d: 90, label: "3M" },
  { d: 180, label: "6M" },
  { d: 365, label: "1Y" },
];

const Correlations = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiText, setAiText] = useState(null);

  const load = useCallback(async (d) => {
    setLoading(true);
    setAiText(null);
    try {
      const { data } = await client.get(`/markets/correlations?days=${d}`);
      setData(data);
    } catch (e) {
      console.error("correlations load failed", e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const generateAI = async () => {
    setAiLoading(true);
    try {
      const { data } = await client.get(`/markets/correlations?days=${days}&explain=true`);
      setAiText(data.explanation || "No explanation returned.");
    } catch (e) {
      setAiText("AI explanation unavailable: " + (e.response?.data?.detail || e.message));
    } finally { setAiLoading(false); }
  };

  if (loading && !data) {
    return (
      <section className="glass p-6" data-testid="correlations-loading">
        <div className="h-6 w-1/3 bg-white/5 rounded animate-pulse mb-4" />
        <div className="h-64 bg-white/5 rounded animate-pulse" />
      </section>
    );
  }
  if (!data) return null;

  const { keys, matrix, summary, n_points } = data;

  return (
    <section className="glass p-6 lg:p-8" data-testid="correlations-section">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500">
            <Activity size={12} /> Cross-Asset Correlations
          </div>
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight mt-2">How everything moves together</h2>
          <p className="text-neutral-400 text-sm mt-1">
            Pearson correlation of daily returns over {n_points} trading days. <span className="up">+1</span> moves in lockstep · <span className="text-neutral-400">0</span> independent · <span className="down">-1</span> moves opposite.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {RANGES.map((r) => (
            <button
              key={r.d}
              data-testid={`corr-range-${r.d}`}
              onClick={() => setDays(r.d)}
              className={`px-3 py-1.5 rounded-lg text-xs font-mono-num transition-colors ${days === r.d ? "bg-white text-black" : "bg-white/5 text-neutral-300 hover:bg-white/10"}`}
            >
              {r.label}
            </button>
          ))}
          <button
            data-testid="corr-ai-explain"
            onClick={generateAI}
            disabled={aiLoading}
            className="btn-accent !py-2 !px-4 text-xs inline-flex items-center gap-2"
          >
            <Sparkles size={12} /> {aiLoading ? "Analyzing…" : aiText ? "Regenerate" : "Explain with AI"}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs font-mono-num">
          <thead>
            <tr>
              <th className="p-2"></th>
              {keys.map((k) => (
                <th key={k} className="p-2 text-neutral-400 font-medium">{k}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.map((row, i) => (
              <tr key={keys[i]}>
                <th className="p-2 text-left text-neutral-400 font-medium pr-3">{keys[i]}</th>
                {row.map((v, j) => {
                  const c = cellColor(v);
                  return (
                    <td
                      key={`${i}-${j}`}
                      data-testid={`corr-${keys[i]}-${keys[j]}`}
                      style={{ background: c.bg, color: c.fg }}
                      className="p-3 text-center border border-black/40 transition-all hover:scale-105"
                      title={`${keys[i]} vs ${keys[j]}: ${v !== null ? v.toFixed(3) : "n/a"}`}
                    >
                      {v !== null ? v.toFixed(2) : "—"}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Period summary chips */}
      <div className="mt-5 flex flex-wrap gap-2">
        {summary.map((s) => (
          <div
            key={s.key}
            data-testid={`corr-summary-${s.key}`}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-xs"
          >
            <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
            <span className="text-neutral-200">{s.name}</span>
            <span className={s.period_change_pct >= 0 ? "up" : "down"}>{fmtPct(s.period_change_pct)}</span>
          </div>
        ))}
      </div>

      {/* AI explanation */}
      {aiText && (
        <div className="mt-6 pt-6 border-t border-white/5">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={12} className="text-blue-400" />
            <div className="text-xs uppercase tracking-widest text-neutral-500">Claude's Take</div>
          </div>
          <div className="text-sm text-neutral-200 leading-relaxed max-w-3xl">
            <ReactMarkdown
              components={{
                p: ({ node, ...p }) => <p className="mb-3" {...p} />,
                em: ({ node, ...p }) => <em className="text-neutral-500 not-italic block mt-3 text-xs" {...p} />,
                strong: ({ node, ...p }) => <strong className="text-white font-medium" {...p} />,
              }}
            >
              {aiText}
            </ReactMarkdown>
          </div>
        </div>
      )}
    </section>
  );
};

export default Correlations;
